package pl.nickers.match3.ui

import androidx.compose.foundation.Canvas
import androidx.compose.foundation.Image
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.RoundRect
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.Path
import androidx.compose.ui.graphics.PathEffect
import androidx.compose.ui.graphics.StrokeCap
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.IntOffset
import androidx.compose.ui.unit.dp
import kotlin.math.min
import kotlin.math.roundToInt
import org.jetbrains.compose.resources.painterResource
import pl.nickers.match3.JellyCell
import pl.nickers.match3.ecs.LaserDirection

@Composable
internal fun JellyItem(
    cell: JellyCell,
    cellSize: Dp,
    isSelected: Boolean,
    offsetX: Float,
    offsetY: Float,
    scale: Float = 1f,
    alpha: Float = 1f,
) {
    Box(
        modifier = Modifier
            .size(cellSize)
            .offset { IntOffset(offsetX.roundToInt(), offsetY.roundToInt()) }
            .graphicsLayer {
                scaleX = scale
                scaleY = scale
                this.alpha = alpha
            }
            .then(
                if (isSelected) Modifier.border(3.dp, Color.White)
                else Modifier
            ),
        contentAlignment = Alignment.Center,
    ) {
        if (cell.isEmpty) return
        if (cell.isSpawnPoint) {
            if (cell.spawnPointVisible) SpawnPointItem(cellSize = cellSize)
            return
        }
        if (cell.isBomb) {
            BombItem(
                cellSize = cellSize,
                bodyImage = cell.bodyImage,
                faceImage = cell.faceImage,
                laserDirection = cell.laserDirection,
            )
        } else if (cell.isIceCube) {
            IceCubeItem(cellSize = cellSize, bodyImage = cell.bodyImage)
        } else {
            Image(
                painter = painterResource(cell.bodyImage.toBodyDrawableRes()),
                contentDescription = "Jelly body",
                modifier = Modifier.size(cellSize),
                contentScale = ContentScale.Fit,
            )
            Image(
                painter = painterResource(cell.faceImage.toFaceDrawableRes()),
                contentDescription = "Jelly face",
                modifier = Modifier.size(cellSize),
                contentScale = ContentScale.Fit,
            )
            if (cell.isCursed) CursedOverlay(cellSize = cellSize)
        }
    }
}

/**
 * E5: shadowy veil over a cursed ingredient — a dark violet disc with a deeper rim,
 * deliberately distinct from the golden Crystallized Amber sprite (which replaces the
 * body image entirely rather than overlaying it).
 */
@Composable
private fun CursedOverlay(cellSize: Dp) {
    Canvas(modifier = Modifier.size(cellSize)) {
        val s = size.width
        val center = Offset(s / 2f, s / 2f)
        drawCircle(
            color = Color(0x99311B5E),
            radius = s * 0.42f,
            center = center,
        )
        drawCircle(
            color = Color(0xCC1A0E33),
            radius = s * 0.42f,
            center = center,
            style = Stroke(width = s * 0.06f),
        )
    }
}

@Composable
private fun BombItem(
    cellSize: Dp,
    bodyImage: String,
    faceImage: String,
    laserDirection: LaserDirection? = null,
) {
    Box(contentAlignment = Alignment.Center) {
        Image(
            painter = painterResource(bodyImage.toBodyDrawableRes()),
            contentDescription = "Bomb body",
            modifier = Modifier.size(cellSize),
            contentScale = ContentScale.Fit,
        )
        if (laserDirection != null) {
            Canvas(modifier = Modifier.size(cellSize)) {
                val centerX = size.width / 2f
                val centerY = size.height / 2f
                val beamLength = min(size.width, size.height) * 0.62f
                val stroke = min(size.width, size.height) * 0.12f
                when (laserDirection) {
                    LaserDirection.HORIZONTAL -> {
                        drawLine(
                            color = Color.White.copy(alpha = 0.9f),
                            start = Offset(centerX - beamLength / 2f, centerY),
                            end = Offset(centerX + beamLength / 2f, centerY),
                            strokeWidth = stroke,
                            cap = StrokeCap.Round,
                        )
                    }

                    LaserDirection.VERTICAL -> {
                        drawLine(
                            color = Color.White.copy(alpha = 0.9f),
                            start = Offset(centerX, centerY - beamLength / 2f),
                            end = Offset(centerX, centerY + beamLength / 2f),
                            strokeWidth = stroke,
                            cap = StrokeCap.Round,
                        )
                    }
                }
            }
        }
        Image(
            painter = painterResource(faceImage.toFaceDrawableRes()),
            contentDescription = "Bomb face",
            modifier = Modifier.size(cellSize),
            contentScale = ContentScale.Fit,
        )
    }
}

@Composable
private fun IceCubeItem(cellSize: Dp, bodyImage: String) {
    Image(
        painter = painterResource(bodyImage.toBodyDrawableRes()),
        contentDescription = "Ice cube",
        modifier = Modifier.size(cellSize),
        contentScale = ContentScale.Fit,
    )
}

@Composable
private fun SpawnPointItem(cellSize: Dp) {
    Canvas(modifier = Modifier.size(cellSize)) {
        val s = size.width
        val inset = s * 0.1f
        val cornerRadius = s * 0.12f
        val dashLen = s * 0.12f
        val gapLen = s * 0.08f
        val strokeW = s * 0.06f
        val color = Color(0xFFB0BEC5)
        val path = Path().apply {
            addRoundRect(
                RoundRect(
                    left = inset, top = inset,
                    right = s - inset, bottom = s - inset,
                    radiusX = cornerRadius, radiusY = cornerRadius,
                )
            )
        }
        drawPath(
            path = path,
            color = color,
            style = Stroke(
                width = strokeW,
                pathEffect = PathEffect.dashPathEffect(floatArrayOf(dashLen, gapLen)),
            ),
        )
        val cx = s / 2f
        val arrowW = s * 0.28f
        val arrowH = s * 0.22f
        val arrowTop = s / 2f - arrowH / 2f
        val arrowBottom = arrowTop + arrowH
        val arrowPath = Path().apply {
            moveTo(cx - arrowW / 2f, arrowTop)
            lineTo(cx + arrowW / 2f, arrowTop)
            lineTo(cx, arrowBottom)
            close()
        }
        drawPath(arrowPath, color)
    }
}
